import React, { useEffect, useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas'; // Docs: https://www.npmjs.com/package/react-signature-canvas
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';

import { faEraser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    Button, DialogActions, DialogContent, IconButton, TextField
} from '@mui/material';

import { Datepicker, FlexContainer } from 'components';
import { ModalProps } from 'hooks/ModalProvider';
import { SignatureFieldView } from 'models';
import { translate } from 'utils/helpers';
import { ModalHeader } from './ModalHeader';

const SignatureCanvasContainer = styled.div<{disabled: boolean}>`
  width: 280px;
  height: 280px;
  margin: 2rem auto 1rem auto;
  border: 2px solid var(--black);
  filter: ${props => (props.disabled ? 'brightness(75%)' : 'brightness(100%)')};

  canvas {
    width: 100%;
    height: 100%;
    ${props => (props.disabled && 'cursor: not-allowed;')}
  }
`;

export const AddSignatureModal = ({
  defaultValues = new SignatureFieldView(),
  onClose,
  onSubmit
}: ModalProps<SignatureFieldView> & {
  defaultValues: SignatureFieldView
}) => {
    const [canvasActivated, setCanvasActivated] = useState(true);
    const signatureRef = useRef<SignatureCanvas>(null);

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        setValue
    } = useForm<SignatureFieldView>({
        defaultValues,
        mode: 'onBlur'
    });

    const signatureType = watch('type');

    useEffect(() => {
        if (defaultValues.document?.base64Content && signatureRef.current) {
            const image = new Image();
            image.onload = () => {
                const canvas = signatureRef.current?.getCanvas();
                if (canvas) {
                    canvas.getContext('2d')?.drawImage(image, 0, 0);
                    signatureRef.current?.off();
                    setCanvasActivated(false);
                }
            };
            image.src = defaultValues.document.base64Content;
        }
    }, [defaultValues.document?.base64Content]);

    const clearCanvas = () => {
        if (signatureRef.current) {
            signatureRef.current.clear();
            signatureRef.current.on();
            setCanvasActivated(true);
        }
    };

    const handleValidateModal = (formData: SignatureFieldView) => {
        if (!signatureRef.current) return;

        const base64Content = signatureRef.current.getCanvas().toDataURL('image/png');

        onSubmit({
            ...formData,
            document: {
                base64Content,
                name: `${formData.type}_${formData.name}`,
                extension: '.png'
            }
        });
    };

    return (
      <form autoComplete="off" onSubmit={handleSubmit(handleValidateModal)}>
        <ModalHeader onClose={onClose}>{translate('common.sign')}</ModalHeader>

        <DialogContent style={{ paddingTop: '10px' }}>
          <FlexContainer flexDirection="column">
            <Controller
                        control={control}
                        name="name"
                        render={({ field }) => (
                          <TextField
                                error={!!errors.name}
                                helperText={errors.name ? String(translate('errors.required')) : undefined}
                                label={translate('modalSign.name')}
                                required
                                {...field}
                            />
                        )}
                        rules={{ required: true }}
                    />

            <Controller
                        control={control}
                        name="occupation"
                        render={({ field }) => (
                          <TextField
                                error={!!errors.occupation}
                                helperText={errors.occupation ? String(translate('errors.required')) : undefined}
                                label={translate('modalSign.occupation')}
                                required
                                {...field}
                            />
                        )}
                        rules={{ required: true }}
                    />

            <Controller
                        control={control}
                        name="signatureDate"
                        render={({ field: { value, onChange } }) => (
                          <Datepicker
                                disableFuture
                                label="common.signatureDate"
                                name="signatureDate"
                                required
                                value={value}
                                onChange={onChange}
                            />
                        )}
                        rules={{ required: true }}
                    />

            {signatureType === 'operatorSignatureField' && (
            <Controller
                            control={control}
                            name="interventionDate"
                            render={({ field: { value, onChange } }) => (
                              <Datepicker
                                    disableFuture
                                    label="common.interventionDate"
                                    name="interventionDate"
                                    required
                                    value={value}
                                    onChange={onChange}
                                />
                            )}
                            rules={{ required: true }}
                        />
                    )}
          </FlexContainer>

          <SignatureCanvasContainer disabled={!canvasActivated}>
            <SignatureCanvas clearOnResize={false} ref={signatureRef} throttle={5} velocityFilterWeight={0.3}
                    />
          </SignatureCanvasContainer>
        </DialogContent>

        <DialogActions>
          <IconButton type="button" onClick={clearCanvas}>
            <FontAwesomeIcon color="var(--blue)" icon={faEraser} />
          </IconButton>
          <Button type="button" onClick={onClose}>{translate('button.cancel')}</Button>
          <Button color="primary" type="submit">{translate('button.saveSignature')}</Button>
        </DialogActions>
      </form>
    );
};
